<template>
  <!-- SERVE — one row of Home's Store-health scorecard (HealthRow); the other service
       points are sub-rows on the same scale as checkout. -->
  <div class="gauge-row">
    <HealthRow
      :eyebrow="$t('gauges.eyebrow.serve')"
      :subtitle="$t('gauges.subtitle.serve')"
      :accent="accent"
      :disabled="!isGaugeEnabled"
      :status="rowStatus"
      :keep-value="rowBuilding"
      :value="rowValue"
      :value-sub="rowValueSub"
      :meta="rowMeta"
      :pct="svcHeadline"
      :pct-sub="rowPctSub"
      :delta="deltaLong"
      bar="ladder"
      :rungs="rowRungs"
      :axis-max="rowAxisMax"
      :sub-rows="rowSubRows"
      @help="showExplain = true"
    >
      <template #notes>
        <span v-if="svcAtRisk" class="row-atrisk">
          <i class="bi bi-exclamation-circle"></i>
          <strong>≈ {{ currency }} {{ formatMoney(svcAtRisk) }}</strong> {{ $t('gauges.serve.atRisk') }} <em>{{ $t('gauges.row.est') }}</em>
        </span>
        <span v-else class="row-atrisk row-atrisk--ok">
          <i class="bi bi-check-circle"></i> {{ $t('gauges.serve.noRisk') }}
        </span>
      </template>
    </HealthRow>
    <StoreServiceHelpModal
      :show="showExplain"
      :segment-key="segmentKey"
      @close="showExplain = false"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import StoreServiceHelpModal from './StoreServiceHelpModal.vue'
import HealthRow from './ui/HealthRow.vue'
import { computeServiceGauge } from '@/prioritization/gauges'
import { currentLanguage } from '@/i18n'
import { formatNumber, formatDuration } from '@/i18n/format'
import { rungLabel } from '@/i18n/rungs'
import { zoneTypeLabel } from '@/i18n/zoneTypes'

const props = defineProps({
  fixedWidgetsData: { type: Object, required: true },
  fixedWidgetsCompareToData: { type: Object, default: null },
  segmentKey: { type: String, default: 'default' },
  currency: { type: String, default: '€' },
  isActive: { type: Boolean, default: true },
  // used in "±Xpp vs {prevPeriodLabel}"
  prevPeriodLabel: { type: String, default: '' },
  accent: { type: String, default: '#36910d' },
  hasWaitingZones: { type: Boolean, default: true },
  waitingLaneCount: { type: Number, default: 0 },
  // Service / Info / Pick Up zones are conceptually queue-side too — surfaced in
  // the footer count and, when the backend emits their ladders, as mini-climbs.
  serviceZoneCount: { type: Number, default: 0 },
  infoZoneCount:    { type: Number, default: 0 },
  pickUpZoneCount:  { type: Number, default: 0 },
  // Shared scale with the other ladder rows (null → own scale).
  // Our own need is emitted as `axis-top` so the host can pick the shared max.
  axisMax: { type: Number, default: null },
})
const emit = defineEmits(['axis-top'])
const { t } = useI18n()

const showExplain = ref(false)
const hasWaitingZones = computed(() => !!props.hasWaitingZones)
const isGaugeEnabled = computed(() => props.isActive && hasWaitingZones.value)

// Single source of truth for the SERVE math — prioritization/gauges.js
// (parity-tested against the backend signals/gauges.py).
const gauge = computed(() =>
  computeServiceGauge(props.fixedWidgetsData, props.segmentKey, {
    hasWaitingZones: hasWaitingZones.value,
    isActive: props.isActive,
  })
)

const pctRounded = computed(() => gauge.value.pctRounded)

// Checkout revenue exposed by over-target waits — soft, ranged (est.) flag only.
const lostServiceEur = computed(() => gauge.value.lostServiceEur)

// --- SERVE four-rung climb (inverted; reads the backend's finished _service.serve) ---
const serve = computed(() => props.fixedWidgetsData?._service?.serve || null)
// The store-level ladder fields live under serve.climb (signals/gauges compute_service_gauge
// flattens only headlinePct / peer / ceiling ticks / ownBest / cohort waits). Reading them
// at the top level left every store on "building your baseline" (b261744, seen 2026-10-23).
// No wait data at all: climb is null -> still "building", correctly.
const climb = computed(() => serve.value?.climb || {})
const svcHeadline = computed(() => Math.round(serve.value?.headlinePct ?? pctRounded.value ?? 0))

// Previous-period delta: change in the SAME own-best headline the card shows.
// The backend doesn't emit four-rung signals for compare slots, so we recompute
// the previous headline client-side (like ATTRACT does): own-best is period-
// independent, so prevHeadline = your-best-wait ÷ previous raw wait — the same
// inverted formula, same anchor. Falls back to a directly-supplied compare
// headline if present; null (no delta) when neither input is available.
const svcPrevHeadline = computed(() => {
  const direct = props.fixedWidgetsCompareToData?._service?.serve?.headlinePct
  if (Number.isFinite(direct)) return Math.round(direct)
  const ownBest = serve.value?.ownBestWaitSec
  const prevWait = Number(props.fixedWidgetsCompareToData?.average_waiting_time)
  if (Number.isFinite(ownBest) && ownBest > 0 && Number.isFinite(prevWait) && prevWait > 0) {
    return Math.round(Math.min(100, (ownBest / prevWait) * 100))
  }
  return null
})
const deltaPts = computed(() =>
  svcPrevHeadline.value == null ? null : svcHeadline.value - svcPrevHeadline.value
)
const deltaLong = computed(() => {
  if (!props.prevPeriodLabel || deltaPts.value == null) return null
  const v = deltaPts.value
  const sign = v > 0 ? '+' : v < 0 ? '−' : '±'
  const arrow = v > 0 ? '▲' : v < 0 ? '▼' : '◆'
  return t('gauges.row.delta', { arrow, sign, abs: Math.abs(v), label: props.prevPeriodLabel })
})
const svcAxisMax = computed(() => {
  const s = serve.value
  const top = Math.max(100, svcHeadline.value, s?.ceilingTickPct ?? 0, s?.peerTickPct ?? 0)
  return Math.max(100, Math.ceil((top * 1.06) / 10) * 10)
})
// SERVE = protect (docs/11): the outcome is TIME, not a recovered euro.
const svcActiveRung = computed(() => climb.value.activeRung ?? 'your best weeks')
const svcAtFloor = computed(() => !!climb.value.atFloor)
const svcReductionMin = computed(() => {
  const r = climb.value.waitReductionMin
  return (r != null) ? Number(r) : null
})
const svcAtRisk = computed(() => serve.value?.atRiskEur ?? lostServiceEur.value ?? null)
const svcHeroSub = computed(() => {
  if (svcAtFloor.value) return t('gauges.serve.heroFloor')
  // No four-rung yet (backend still calibrating your best-weeks baseline).
  if (svcReductionMin.value == null) return t('gauges.serve.heroBuilding')
  return t('gauges.serve.heroWithin', { rung: rungLabel(svcActiveRung.value, { form: 'in' }) })
})

// --- The row (on a possibly shared scale) ---
// Row copy speaks in WAIT TIME, the way a manager thinks: the gap is "2 min longer
// wait than your best weeks" (not "−2 min", which reads like a gain), and the %
// is explained by the two waits it compares ("6 min wait now · 4 min in your best weeks").
// Baseline still building (no best-weeks wait yet): there is nothing to compare the
// wait WITH, so the row shows today's wait and says so — no bar, no % and no at-risk
// line (a "% of your best" with no best is meaningless; it rendered 27% vs a 4% rung).
const rowBuilding = computed(() =>
  isGaugeEnabled.value && !svcAtFloor.value && svcReductionMin.value == null)
const rowStatus = computed(() => {
  if (!isGaugeEnabled.value) return { title: t('gauges.row.notConfigured'), sub: t('gauges.serve.notConfiguredSub') }
  if (rowBuilding.value) return {
    title: t('gauges.serve.buildingTitle'),
    sub: t('gauges.serve.buildingSub'),
  }
  return null
})
const rowValue = computed(() => {
  if (rowBuilding.value) {
    const cur = Number(climb.value.currentWaitSec)
    return Number.isFinite(cur) && cur > 0 ? fmtDur(cur / 60) : '—'
  }
  return (!svcAtFloor.value && svcReductionMin.value != null) ? fmtDur(svcReductionMin.value)
    : (svcAtFloor.value ? t('gauges.serve.atBest') : '—')
})
const rowValueSub = computed(() => {
  if (rowBuilding.value) return t('gauges.serve.waitNow')
  if (svcAtFloor.value) return t('gauges.serve.floorSub')
  if (svcReductionMin.value == null) return svcHeroSub.value
  return t('gauges.serve.longer', { rung: rungLabel(svcActiveRung.value, { form: 'in' }) })
})
const rowPctSub = computed(() => {
  const cur = Number(climb.value.currentWaitSec)
  const best = Number(serve.value?.ownBestWaitSec)
  if (Number.isFinite(cur) && cur > 0 && Number.isFinite(best) && best > 0) {
    return t('gauges.serve.pctSub', { now: fmtDur(cur / 60), best: fmtDur(best / 60) })
  }
  return t('gauges.serve.pctSubFallback')
})
const rowRungs = computed(() => {
  const s = serve.value
  const rows = [{ key: 'best', name: t('gauges.rungs.yourBest'), pct: 100 }]
  if (s?.peerTickPct != null) rows.push({ key: 'peers', name: t('gauges.rungs.peers'), pct: s.peerTickPct })
  if (s?.ceilingTickPct != null) rows.push({ key: 'bic', name: t('gauges.rungs.bic'), pct: s.ceilingTickPct })
  return rows
})
const rowSubRows = computed(() =>
  (props.fixedWidgetsData?._service?.waiting || [])
    .filter((w) => w && w.zoneType !== 4)
    .map((w) => {
      const rungs = [{ key: 'best', pct: 100 }]
      if (w.peerTickPct != null) rungs.push({ key: 'peers', pct: w.peerTickPct })
      if (w.ceilingTickPct != null) rungs.push({ key: 'bic', pct: w.ceilingTickPct })
      const atBest = !!w.atFloor
      return {
        key: w.zoneType,
        name: waitZoneName(w),
        atBest,
        // Spelled out: the minutes the wait here runs ABOVE its own best weeks — a bare
        // "−0.8 min" read as a change, not as a gap to close.
        text: atBest ? t('gauges.serve.asFast')
          : (w.waitReductionMin != null ? t('gauges.serve.waitsLonger', { dur: fmtDur(w.waitReductionMin) }) : ''),
        pct: Math.round(w.headlinePct ?? 0),
        rungs,
      }
    }))
const rowAxisMax = computed(() => props.axisMax || svcAxisMax.value)
const rowMeta = computed(() => {
  const part = (n, key) => `${n} <strong>${t(`gauges.zoneNames.${key}`, n)}</strong>`
  const parts = [part(props.waitingLaneCount, 'checkoutWaiting')]
  if (props.serviceZoneCount) parts.push(part(props.serviceZoneCount, 'service'))
  if (props.infoZoneCount) parts.push(part(props.infoZoneCount, 'info'))
  if (props.pickUpZoneCount) parts.push(part(props.pickUpZoneCount, 'pickUp'))
  return t('gauges.row.basedOn', { list: parts.join(', ') })
})
watch(
  () => {
    if (!isGaugeEnabled.value || rowBuilding.value) return null
    const s = serve.value
    const subTops = rowSubRows.value.flatMap((r) => [r.pct, ...r.rungs.map((x) => x.pct)])
    return Math.max(100, svcHeadline.value, s?.ceilingTickPct ?? 0, s?.peerTickPct ?? 0, ...subTops)
  },
  (top) => emit('axis-top', top),
  { immediate: true },
)

// A waiting service point's name on screen. The backend's zoneTypeName is the type
// name without " Zone" ('Service Waiting'); English shows exactly that, Dutch the
// glossary name ('Service-wachtzone').
function waitZoneName(w) {
  if (currentLanguage() !== 'en' && w.zoneType != null) {
    const label = zoneTypeLabel(w.zoneType)
    if (typeof label === 'string' && label !== String(w.zoneType)) return label
  }
  return w.zoneTypeName || t('gauges.serve.waitFallback')
}

// A duration the way people say it: "48 sec", "2 min", "1 min 10 sec" — never decimal
// minutes ("0.8 min"). Input is minutes (the backend's waitReductionMin).
function fmtDur(min) {
  const total = Math.round(Number(min) * 60)
  if (!Number.isFinite(total)) return ''
  if (currentLanguage() !== 'en') return formatDuration(total)
  if (total < 60) return `${total} sec`
  const m = Math.floor(total / 60), s = total % 60
  return s ? `${m} min ${s} sec` : `${m} min`
}

function formatMoney(value) {
  const v = Math.round(Number(value || 0))
  if (!Number.isFinite(v)) return '0'
  // English keeps the browser-locale grouping it always had; Dutch is 1.234.
  if (currentLanguage() !== 'en') return formatNumber(v)
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 })
}
</script>

<style scoped>
/* The at-risk note under the checkout bar */
.row-atrisk { display: inline-flex; align-items: center; gap: 6px; color: #9a5b0c; font-size: 12px; }
.row-atrisk em { font-style: normal; color: #b8925a; }
.row-atrisk--ok { color: #2c6f58; }
</style>
